// WhatsApp-ready message builders for order copy-to-clipboard.
// All content comes strictly from data already stored in the order.
// NO invention, NO catalog lookups, NO AI. Frozen prices only.

const STATUS_LABEL = {
  RECEIVED: "Recibido",
  IN_DIAGNOSIS: "En diagnóstico",
  WAITING_APPROVAL: "Esperando aprobación",
  WAITING_PARTS: "Esperando repuesto",
  IN_REPAIR: "En reparación",
  TESTING: "En pruebas",
  REPAIRED: "Reparado",
  IN_TRANSFER: "En traslado a sucursal",
  READY: "Listo para retirar",
  DELIVERED: "Retirado por cliente",
  NOT_REPAIRABLE: "No reparable",
  CLIENT_REJECTED: "Cliente rechazó reparación",
  IN_WARRANTY: "En garantía",
};

const BUDGET_STATUS_LABEL = {
  PENDING_QUOTE: "Pendiente de presupuesto",
  QUOTE_SENT: "Presupuesto enviado",
  APPROVED: "Aprobado por cliente",
  REJECTED: "Rechazado por cliente",
  AUTHORIZED: "Reparación autorizada",
  COMPLETED: "Reparación completada",
};

const BRAND = "Punto Play / PuntoCell";

function equipmentLine(order) {
  const parts = [];
  if (order.brand) parts.push(order.brand);
  if (order.model) parts.push(order.model);
  if (parts.length === 0) {
    if (order.category?.name) parts.push(order.category.name);
    if (order.subcategory?.name) parts.push(order.subcategory.name);
  }
  return parts.join(" ").trim();
}

function customerName(order) {
  return order.customer?.name || "";
}

/** Diagnóstico message. Returns null if there is no confirmed diagnosis.
 *  Uses `diagnosis_modality`: WRITTEN | VISUAL | BOTH. Falls back to legacy `diagnosis` text. */
export function buildDiagnosisMessage(order) {
  const modality = order.diagnosis_modality || (order.diagnosis ? "WRITTEN" : "NONE");
  const diag = (order.diagnosis || "").trim();
  const hasMedia = (order.diagnosis_media_ids || []).length > 0;
  if (modality === "NONE" || (modality === "WRITTEN" && !diag) || (modality === "VISUAL" && !hasMedia)) {
    return null;
  }
  const lines = [
    `🔧 *Diagnóstico - ${BRAND}*`,
    "",
    `📋 Orden: *${order.order_number}*`,
  ];
  const eq = equipmentLine(order);
  if (eq) lines.push(`📱 Equipo: ${eq}`);
  const cn = customerName(order);
  if (cn) lines.push(`👤 Cliente: ${cn}`);
  lines.push("");
  if (modality === "WRITTEN") {
    lines.push("🔍 *Diagnóstico:*");
    lines.push(diag);
  } else if (modality === "VISUAL") {
    lines.push("📸 *Diagnóstico:*");
    lines.push("Se adjunta evidencia visual del diagnóstico de la reparación.");
  } else if (modality === "BOTH") {
    lines.push("🔍 *Diagnóstico:*");
    lines.push(diag);
    lines.push("");
    lines.push("📸 Se cuenta además con evidencia visual del diagnóstico.");
  }
  if (order.status && STATUS_LABEL[order.status]) {
    lines.push("");
    lines.push(`📌 *Estado:* ${STATUS_LABEL[order.status]}`);
  }
  return lines.join("\n");
}

// Mapea el estado ACTUAL de la orden a la línea que verá el cliente en WhatsApp.
// El mensaje del presupuesto debe reflejar dinámicamente el estado real,
// nunca el `budget_status` (que sólo indica si fue aprobado por el cliente).
function orderStatusLine(order) {
  const s = order.status;
  if (!s) return "";
  if (s === "REPAIRED") return "Reparado — Listo para retirar";
  return STATUS_LABEL[s] || s;
}

/** Budget message. Returns null if there are no items in the budget. */
export function buildBudgetMessage(order, budget) {
  const items = (budget?.items || []);
  if (items.length === 0) return null;
  const lines = [
    `💰 *${BRAND} - Presupuesto*`,
    "",
    `📋 Orden: *${order.order_number}*`,
  ];
  const eq = equipmentLine(order);
  if (eq) lines.push(`📱 Equipo: ${eq}`);
  const cn = customerName(order);
  if (cn) lines.push(`👤 Cliente: ${cn}`);
  lines.push("");
  lines.push("🛠 *Reparación:*");
  for (const it of items) {
    const price = Number(it.unit_price || 0);
    const qty = it.quantity || 1;
    const sub = Number(it.subtotal ?? price * qty);
    const qtyPart = qty > 1 ? ` × ${qty}` : "";
    lines.push(`• ${it.name}${qtyPart} — $${sub.toFixed(2)}`);
  }
  const total = Number(budget.total ?? items.reduce((a, i) => a + (i.subtotal || 0), 0));
  lines.push("");
  lines.push(`💵 *Total: $${total.toFixed(2)}*`);
  // Estado DINÁMICO del equipo (order.status), no del budget_status.
  const statusLine = orderStatusLine(order);
  if (statusLine) {
    lines.push(`📌 *Estado:* ${statusLine}`);
  }
  return lines.join("\n");
}

/** Ready-to-pick-up notice. Se envía cuando la reparación está terminada y
 *  el equipo queda listo para que el cliente lo retire.
 *  Incluye equipo, orden, total (si hay presupuesto) y hora aproximada. */
export function buildReadyMessage(order, budget) {
  const lines = [
    `✅ *${BRAND}*`,
    "",
    `¡Buenas noticias! Tu equipo ya está *Listo para retirar* 🎉`,
    "",
    `📋 Orden: *${order.order_number}*`,
  ];
  const eq = equipmentLine(order);
  if (eq) lines.push(`📱 Equipo: ${eq}`);
  const cn = customerName(order);
  if (cn) lines.push(`👤 Cliente: ${cn}`);
  const items = budget?.items || [];
  if (items.length > 0) {
    const total = Number(budget.total ?? items.reduce((a, i) => a + (i.subtotal || 0), 0));
    if (total > 0) {
      lines.push("");
      lines.push(`💵 *Total a pagar: $${total.toFixed(2)}*`);
    }
  }
  lines.push("");
  lines.push("Podés pasar a retirarlo en horario de atención. ¡Te esperamos!");
  return lines.join("\n");
}

/** Warranty message: enviado al cliente DESPUÉS de confirmar el retiro.
 *  - fecha_retiro: usar `order.pickup_at` (real).
 *  - servicios: items del presupuesto oficial (budget).
 *  - public_url: enlace oficial `/qr/{token}` (usar dominio producción).
 */
export function buildWarrantyMessage(order, publicUrl, budget) {
  const cn = customerName(order) || "";
  const eq = equipmentLine(order) || "Tu equipo";
  const dateStr = order.pickup_at
    ? new Date(order.pickup_at).toLocaleDateString("es-AR", {
        day: "2-digit", month: "2-digit", year: "numeric",
      })
    : "—";
  const items = (budget?.items || []);
  const serviciosLine = items.length > 0
    ? items.map((it) => it.name).filter(Boolean).join(", ")
    : "Servicio técnico";

  const lines = [];
  lines.push("🛠️ ¡Reparación entregada! ✅");
  lines.push("");
  lines.push(`Hola, ${cn} 👋`);
  lines.push("Gracias por confiar tu equipo en PuntoPlay Workshop. 💙");
  lines.push("");
  lines.push(`📋 Orden: *${order.order_number}*`);
  lines.push(`🎮 Equipo: ${eq}`);
  lines.push(`🔧 Reparación: ${serviciosLine}`);
  lines.push(`📅 Entregado: ${dateStr}`);
  lines.push("");
  lines.push("🛡️ Tu reparación cuenta con *30 días de garantía* a partir de la fecha de entrega.");
  lines.push("");
  lines.push("Puedes consultar la información de tu reparación y su seguimiento aquí:");
  lines.push(`🔗 ${publicUrl}`);
  lines.push("");
  lines.push("Si necesitas cualquier ayuda adicional con tu equipo, estamos a la orden. 🙌");
  lines.push("");
  lines.push("¡Gracias por elegir PuntoPlay! 🎮✨");
  return lines.join("\n");
}


/** Share-order message: envía al cliente el enlace público (mismo destino que el QR).
 *  El mensaje es fijo y humano, no depende de que existan diagnóstico ni presupuesto. */
export function buildShareOrderMessage(order, publicUrl) {
  const cn = customerName(order) || "";
  const eq = equipmentLine(order) || "";
  const lines = [];
  lines.push(`Hola ${cn} 👋`.trim());
  lines.push("");
  lines.push("Te compartimos el acceso digital a tu orden de reparación:");
  lines.push("");
  lines.push(`🔧 Orden: *${order.order_number}*`);
  if (eq) lines.push(`📱 Equipo: ${eq}`);
  lines.push("");
  lines.push("Puedes consultar el estado y seguimiento de tu reparación aquí:");
  lines.push(publicUrl);
  lines.push("");
  lines.push("PuntoPlay Workshop");
  return lines.join("\n");
}


/** Full update message. Skips sections without data. */
export function buildFullUpdateMessage(order, budget) {
  const lines = [
    `🔧 *${BRAND}*`,
    "",
    `📋 Orden: *${order.order_number}*`,
  ];
  const eq = equipmentLine(order);
  if (eq) lines.push(`📱 Equipo: ${eq}`);
  const statusLine = orderStatusLine(order);
  if (statusLine) {
    lines.push(`📌 *Estado:* ${statusLine}`);
  }
  const diag = (order.diagnosis || "").trim();
  if (diag) {
    lines.push("");
    lines.push("🔍 *Diagnóstico:*");
    lines.push(diag);
  }
  const items = budget?.items || [];
  if (items.length > 0) {
    lines.push("");
    lines.push("💰 *Presupuesto:*");
    for (const it of items) {
      const qty = it.quantity || 1;
      const sub = Number(it.subtotal ?? (it.unit_price || 0) * qty);
      const qtyPart = qty > 1 ? ` × ${qty}` : "";
      lines.push(`• ${it.name}${qtyPart} — $${sub.toFixed(2)}`);
    }
    const total = Number(budget.total ?? items.reduce((a, i) => a + (i.subtotal || 0), 0));
    lines.push(`💵 *Total: $${total.toFixed(2)}*`);
    const bs = budget.budget_status || order.budget_status;
    if (bs && BUDGET_STATUS_LABEL[bs]) {
      lines.push(`(${BUDGET_STATUS_LABEL[bs]})`);
    }
  }
  return lines.join("\n");
}

/** Cross-platform clipboard copy with Safari/older-browser fallback.
 *  Returns Promise<boolean>. */
export async function copyToClipboard(text) {
  if (text == null) return false;
  const str = String(text);
  // Modern secure-context Clipboard API
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(str);
      return true;
    }
  } catch (e) { /* fallthrough */ }
  // Fallback: hidden textarea + execCommand (works on iOS/older Chrome)
  try {
    const ta = document.createElement("textarea");
    ta.value = str;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    // iOS Safari requires the range selection to be made explicitly
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch (e) {
    return false;
  }
}


/**
 * Normaliza un número de teléfono a formato E.164 para wa.me (sin '+').
 * Soporta Venezuela por defecto:
 *  - '04141234567'  → '584141234567'
 *  - '4141234567'   → '584141234567'
 *  - '+584141234567' o '584141234567' → '584141234567'
 *  - Cualquier otro con +XX conservado (se retira el '+').
 * Devuelve '' si no hay dígitos suficientes.
 */
export function normalizePhoneForWa(rawPhone) {
  if (!rawPhone) return "";
  // Detectar prefijo internacional explícito
  const trimmed = String(rawPhone).trim();
  const hasIntlPrefix = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";

  // Ya viene con código país explícito (+…)
  if (hasIntlPrefix) return digits;

  // Venezuela: 04XXXXXXXXX (11 dígitos empezando con 0)
  if (digits.length === 11 && digits.startsWith("04")) {
    return "58" + digits.slice(1);
  }
  // Venezuela sin cero: 4XXXXXXXXX (10 dígitos empezando con 4)
  if (digits.length === 10 && digits.startsWith("4")) {
    return "58" + digits;
  }
  // Ya viene como 58XXXXXXXXXX
  if (digits.startsWith("58") && digits.length >= 12) return digits;
  // Fallback: devolver dígitos tal cual (por si es otro país sin +)
  return digits;
}

/** Construye la URL wa.me. Si el texto es null/vacío no incluye ?text. */
export function buildWaLink(rawPhone, text) {
  const num = normalizePhoneForWa(rawPhone);
  if (!num) return "";
  const base = `https://wa.me/${num}`;
  if (!text) return base;
  return `${base}?text=${encodeURIComponent(text)}`;
}
